/**
 * stableEmpty — referentially stable empty fallbacks for Zustand selectors.
 *
 * A Zustand selector's return value IS the `useSyncExternalStore` snapshot,
 * compared via `Object.is`. A fallback built INSIDE a selector
 * (`s.x ?? {}` / `s.x ?? []`) yields a NEW reference on every `getSnapshot`
 * call whenever the store value is nullish → `Object.is` is always false →
 * "The result of getSnapshot should be cached" → infinite re-render
 * (Maximum update depth).
 *
 * Share ONE module-level instance instead, so the fallback reference is
 * stable across renders. Behaviour is identical — only the reference
 * stabilises. (This mirrors the inline pattern already in
 * `SiteLandingPage.tsx`.)
 *
 * Usage:
 *   const record = useStore(s => s.someRecord ?? STABLE_EMPTY_RECORD);
 *   const list   = useStore(s => s.someArray  ?? STABLE_EMPTY_ARRAY);
 *
 * For `.filter`/`.map`/`.slice`/etc., do NOT run them inside the selector —
 * select the raw array and derive in a `useMemo` keyed on the array identity.
 */

/** Stable empty object for `?? {}` selector fallbacks. */
export const STABLE_EMPTY_RECORD: Record<string, never> = {};

/** Stable empty array for `?? []` selector fallbacks. */
export const STABLE_EMPTY_ARRAY: never[] = [];

/**
 * Typed stable-empty-array helper. Returns the SAME shared `STABLE_EMPTY_ARRAY`
 * reference every call, narrowed to `readonly T[]` for ergonomic typing at the
 * use site. Reference stability is what matters here, not the element type.
 */
export function stableEmptyArray<T>(): readonly T[] {
  return STABLE_EMPTY_ARRAY as readonly T[];
}

/**
 * Typed stable-empty-record helper. Returns the SAME shared
 * `STABLE_EMPTY_RECORD` reference every call, narrowed to `Record<string, T>`.
 */
export function stableEmptyRecord<T>(): Record<string, T> {
  return STABLE_EMPTY_RECORD as Record<string, T>;
}
